import { MagicDoorFeature } from '~/swagger/portal/Api';

export type NavigationItem = {
  id: string;
  path: string;
  resourceName: string;
  permissionKey?: string;
  canNavigate: boolean;
  children?: NavigationItem[];
  icon?: string;
  selectedIcon?: string;
  realPath?: string;
  requiredFeature?: MagicDoorFeature;
  visibleWhen?: () => boolean;
};

export const NAVIGATION_CONFIG: NavigationItem[] = [
  {
    id: 'Onboarding',
    path: '/onboarding',
    resourceName: 'onboarding',
    canNavigate: true,
  },
  {
    id: 'Home',
    path: '/',
    resourceName: 'home',
    canNavigate: true,
  },
  {
    id: 'Properties',
    path: '/portfolios',
    resourceName: 'property',
    permissionKey: 'properties',
    canNavigate: true,
  },
  {
    id: 'Listing',
    path: '/listings',
    resourceName: 'listing',
    canNavigate: true,
  },
  {
    id: 'Leasing',
    path: '/leasing',
    resourceName: 'leasing',
    canNavigate: false,
    children: [
      {
        id: 'Leases',
        path: '/leasing/leases',
        resourceName: 'lease',
        permissionKey: 'leases',
        canNavigate: true,
      },
      {
        id: 'Lease templates',
        path: '/leasing/lease-templates',
        resourceName: 'lease_template',
        permissionKey: 'lease_templates',
        canNavigate: true,
      },
      {
        id: 'Rental applications',
        path: '/leasing/rental-applications',
        resourceName: 'rental_application',
        permissionKey: 'rental_applications',
        canNavigate: true,
      },
      {
        id: 'Add offline payments',
        path: '/leasing/add-offline-payment',
        resourceName: 'offline_payment',
        permissionKey: 'offline_payments',
        canNavigate: true,
      },
      {
        id: 'Subsidizers',
        path: '/leasing/subsidizers',
        resourceName: 'subsidizer',
        permissionKey: 'subsidizers',
        canNavigate: true,
      },
      {
        id: 'Lease renewal',
        path: '/leasing/lease-renewal',
        resourceName: 'lease_renewal',
        permissionKey: 'lease_renewals',
        canNavigate: true,
      },
      {
        id: 'Insurance',
        path: '/leasing/insurance',
        resourceName: 'insurance',
        permissionKey: 'leasing_insurance',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'Users',
    path: '/users',
    resourceName: 'user',
    canNavigate: false,
    children: [
      {
        id: 'Property managers',
        path: '/users/property-managers',
        resourceName: 'property_manager',
        permissionKey: 'property_managers',
        canNavigate: true,
      },
      {
        id: 'Tenants',
        path: '/users/tenants',
        resourceName: 'tenant',
        permissionKey: 'tenants',
        canNavigate: true,
      },
      {
        id: 'Owners',
        path: '/users/owners',
        resourceName: 'owner',
        permissionKey: 'owners',
        canNavigate: true,
        requiredFeature: MagicDoorFeature.CompanyLedger,
      },
    ],
  },
  {
    id: 'Accounting',
    path: '/accounting',
    resourceName: 'accounting',
    canNavigate: false,
    children: [
      {
        id: 'Bank accounts',
        path: '/accounting/bank-accounts',
        resourceName: 'bank_account',
        permissionKey: 'bank_accounts',
        canNavigate: true,
      },
      {
        id: 'Payables',
        path: '/accounting/bills',
        resourceName: 'bill',
        permissionKey: 'vendor_bills',
        canNavigate: true,
      },
      {
        id: 'Chart of accounts',
        path: '/accounting/chart-of-accounts',
        resourceName: 'chart_of_account',
        permissionKey: 'chart_of_accounts',
        canNavigate: true,
      },
      {
        id: 'Company revenue',
        path: '/accounting/company-revenue',
        resourceName: 'company_revenue',
        permissionKey: 'company_ledger_transfers',
        canNavigate: true,
        requiredFeature: MagicDoorFeature.CompanyLedger,
      },
      {
        id: 'MagicDoor',
        path: '/accounting/magicdoor',
        resourceName: 'magicdoor',
        permissionKey: 'magic_door_bills',
        canNavigate: true,
      },
      {
        id: 'Owner funds',
        path: '/accounting/owner-funds',
        realPath: '/accounting/owner-funds/distributions',
        resourceName: 'owner_fund',
        permissionKey: 'owner_distributions',
        canNavigate: true,
        requiredFeature: MagicDoorFeature.CompanyLedger,
      },
      {
        id: 'Deposit slips',
        path: '/accounting/deposit-slips',
        resourceName: 'deposit_slip',
        permissionKey: 'transactions',
        canNavigate: true,
      },
      {
        id: 'Checks',
        path: '/accounting/checks',
        resourceName: 'check',
        permissionKey: 'transactions',
        canNavigate: true,
      },
      {
        id: 'Manual entries',
        path: '/accounting/manual-entries',
        resourceName: 'manual_entrie',
        permissionKey: 'manual_journal_entries',
        canNavigate: true,
      },
      {
        id: 'Bank ledger transfers',
        path: '/accounting/bank-ledger-transfers',
        resourceName: 'bank_ledger_transfer',
        permissionKey: 'bank_ledger_transfers',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'Reports',
    path: '/reports',
    resourceName: 'report',
    permissionKey: 'reports',
    canNavigate: true,
  },
  {
    id: 'Maintenance',
    path: '/maintenance',
    resourceName: 'maintenance',
    canNavigate: false,
    children: [
      {
        id: 'Vendors',
        path: '/maintenance/vendors',
        resourceName: 'vendor',
        permissionKey: 'vendors',
        canNavigate: true,
      },
      {
        id: 'Maintenance requests',
        path: '/maintenance/maintenance-request',
        resourceName: 'maintenance_request',
        permissionKey: 'maintenance_requests',
        canNavigate: true,
      },
      {
        id: 'Work orders',
        path: '/maintenance/work-orders',
        resourceName: 'work_order',
        permissionKey: 'work_orders',
        canNavigate: true,
      },
      {
        id: 'AI runbooks',
        path: '/maintenance/ai-runbooks',
        resourceName: 'ai_runbook',
        permissionKey: 'maintenance_runbooks',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'Associations',
    path: '/associations',
    resourceName: 'association',
    canNavigate: false,
    children: [
      {
        id: 'Violations',
        path: '/associations/violations',
        resourceName: 'violation',
        permissionKey: 'violations',
        canNavigate: true,
      },
      {
        id: 'Architectural requests',
        path: '/associations/architectural-requests',
        resourceName: 'architectural_request',
        permissionKey: 'hoa_architectural_requests',
        canNavigate: true,
      },
      {
        id: 'Board members',
        path: '/associations/board-members',
        resourceName: 'hoa_board_member',
        permissionKey: 'hoa_board_members',
        canNavigate: true,
      },
      {
        id: 'Surveys',
        path: '/associations/surveys',
        resourceName: 'hoa_survey',
        permissionKey: 'hoa_surveys',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'Communications',
    path: '/communications',
    resourceName: 'communication',
    canNavigate: false,
    children: [
      {
        id: 'Dashboard',
        path: '/communications/dashboard',
        resourceName: 'dashboard',
        permissionKey: 'communications',
        canNavigate: true,
      },
      {
        id: 'Chats',
        path: '/communications/chats',
        resourceName: 'chat',
        permissionKey: 'chats',
        canNavigate: true,
      },
      {
        id: 'Announcements',
        path: '/communications/announcements',
        resourceName: 'announcement',
        permissionKey: 'announcements',
        canNavigate: true,
      },
      {
        id: 'Archived messages',
        path: '/communications/archive-messages',
        resourceName: 'archive_message',
        permissionKey: 'archive_messages',
        canNavigate: true,
      },
      {
        id: 'Templates',
        path: '/communications/templates',
        resourceName: 'letter_template',
        permissionKey: 'letter_templates',
        canNavigate: true,
      },
      {
        id: 'Document templates',
        path: '/communications/document-templates',
        resourceName: 'letter_template',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'AI',
    path: '/ai',
    resourceName: 'ai',
    canNavigate: false,
    children: [
      {
        id: 'Policy handbook',
        path: '/ai/policy-handbook',
        resourceName: 'policy_handbook',
        permissionKey: 'policy_handbooks',
        canNavigate: true,
      },
    ],
  },
  {
    id: 'Settings',
    path: '/settings',
    resourceName: 'settings',
    canNavigate: true,
  },
];

/**
 * Helper function to filter navigation items based on feature flags and visibility conditions
 */
export const filterNavigationItems = (items: NavigationItem[], checkFeature: (feature: MagicDoorFeature) => boolean): NavigationItem[] => {
  return items
    .filter((item) => {
      // Check feature flag
      if (item.requiredFeature && !checkFeature(item.requiredFeature)) {
        return false;
      }

      // Check custom visibility condition
      if (item.visibleWhen && !item.visibleWhen()) {
        return false;
      }

      return true;
    })
    .map((item) => {
      // Recursively filter children
      if (item.children) {
        return {
          ...item,
          children: filterNavigationItems(item.children, checkFeature),
        };
      }
      return item;
    });
};

/**
 * Flatten navigation items into a flat list with full resource names
 */
export const flattenNavigationItems = (
  items: NavigationItem[],
  parentResourceName?: string
): Array<NavigationItem & { fullResourceName: string; parentResourceName?: string }> => {
  const result: Array<NavigationItem & { fullResourceName: string; parentResourceName?: string }> = [];

  for (const item of items) {
    const fullResourceName = item.permissionKey ?? (parentResourceName ? `${parentResourceName}_${item.resourceName}` : item.resourceName);

    result.push({
      ...item,
      fullResourceName,
      parentResourceName,
    });

    if (item.children) {
      result.push(...flattenNavigationItems(item.children, fullResourceName));
    }
  }

  return result;
};
